import { Box, Link } from "@chakra-ui/react"
import * as React from "react"
import {
  FaMicrophone,
  FaMicrophoneSlash,
  FaVideo,
  FaVideoSlash
} from "react-icons/fa"
import { IoMdHand } from "react-icons/io"
import { useRecoilValue } from "recoil"
import { usersSelector, localUserIdAtom } from "../../../recoil/atoms/users"
import { hasPermission } from "../../../util"
import { UserPanel } from "./user-panel"
import { Icon } from "../../common/icons"
import { brandingSelector } from "../../../recoil/atoms/room"
import {
  watchPartyHostIdAtom,
  watchPartyScreenSelector
} from "../../../recoil/atoms/watchPartyScreen"
import { useTranslation } from "react-i18next"
import { showNoVideoSubscribeScreenSelector } from "../../../recoil/atoms/rtc"
import useDeviceManagerChecker from "../../../hooks/useDeviceManagerChecker"

const UserType = window.MeetOnline?.UserType
const UserPermission = window.MeetOnline?.UserPermission

interface UserModeratorViewProps {
  userId: string
  isLast: boolean
  isModerator: boolean
  isAudience: boolean
}

export interface accessList {
  name: string
  hasPermission: boolean
  value: string | number
  togglePermission: () => void
}

const _UserModeratorView = ({
  userId,
  isLast,
  isAudience,
  isModerator
}: UserModeratorViewProps) => {
  const { t } = useTranslation()
  const meetingClient = React.useRef(window.MeetOnline.client()).current
  const roomStyles = useRecoilValue(brandingSelector)

  const user = useRecoilValue(usersSelector(userId))
  const isWatchPartyInProgress = useRecoilValue(watchPartyScreenSelector)
  const wpHostId = useRecoilValue(watchPartyHostIdAtom)
  const { toggleLocalCamera, toggleLocalMicrophone } = useDeviceManagerChecker()
  const _localUserId = useRecoilValue(localUserIdAtom)
  const _isLocalUser = _localUserId === userId
  const isMediaDisabled = useRecoilValue(showNoVideoSubscribeScreenSelector)

  const {
    id,
    name,
    type,
    permissions,
    micEnabled,
    camEnabled,
    screenShareEnabled,
    handUp,
    color,
    avatar
  } = user

  const isShareScreen = React.useMemo(
    () => screenShareEnabled || (isWatchPartyInProgress && id === wpHostId),
    [id, screenShareEnabled, isWatchPartyInProgress, wpHostId]
  )

  const requestMicToggle = React.useCallback(
    async (event: React.MouseEvent) => {
      event.stopPropagation()
      // check if local user
      _isLocalUser
        ?  toggleLocalMicrophone()
        : meetingClient?.toggleMic(micEnabled, userId)
    },
    [_isLocalUser, meetingClient, micEnabled, toggleLocalMicrophone, userId]
  )

  const requestCamToggle = React.useCallback(
    async (event: React.MouseEvent) => {
      event.stopPropagation()

      // check if local user
      _isLocalUser
        ? toggleLocalCamera()
        : meetingClient?.toggleCam(camEnabled, userId)
    },
    [_isLocalUser, toggleLocalCamera, meetingClient, camEnabled, userId]
  )

  const dropHand = React.useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation()
      meetingClient?.sendHandRaised(false, id)
    },
    [id, meetingClient]
  )


  const micDisplay = React.useMemo(() => {
    const isEnabled = _isLocalUser ? !isMediaDisabled : micEnabled
    return (
      <Box
        ml="0.2em !important"
        onClick={isEnabled ? requestMicToggle : undefined}
        opacity={isEnabled ? 1 : 0.5}
        textColor={
          roomStyles?.servicePanelElementPrimaryText
            ? roomStyles?.servicePanelElementPrimaryText
            : micEnabled
            ? "gray.900"
            : "gray.500"
        }
        display="flex"
      >
        <Icon
          src={micEnabled ? FaMicrophone : FaMicrophoneSlash}
          fontSize="1em"
          cursor={isEnabled ? "pointer" : "not-allowed"}
        />
      </Box>
    )
  }, [
    micEnabled,
    requestMicToggle,
    roomStyles?.servicePanelElementPrimaryText,
    _isLocalUser,
    isMediaDisabled
  ])

  const camDisplay = React.useMemo(() => {
    const isEnabled = _isLocalUser ? !isMediaDisabled : camEnabled
    return (
      <Box
        ml="0.2em !important"
        onClick={isEnabled ? requestCamToggle : undefined}
        opacity={isEnabled ? 1 : 0.5}
        display="flex"
        textColor={
          roomStyles?.servicePanelElementPrimaryText
            ? roomStyles?.servicePanelElementPrimaryText
            : camEnabled
            ? "gray.900"
            : "gray.500"
        }
      >
        <Icon
          src={camEnabled ? FaVideo : FaVideoSlash}
          fontSize="1em"
          cursor={isEnabled ? "pointer" : "not-allowed"}
        />
      </Box>
    )
  }, [
    camEnabled,
    requestCamToggle,
    roomStyles?.servicePanelElementPrimaryText,
    _isLocalUser,
    isMediaDisabled
  ])

  const handRaisedDisplay = React.useMemo(
    () =>
      handUp === true ? (
        <Link onClick={dropHand} color="teal" display="flex">
          <Icon src={IoMdHand} fontSize="1em" color="#FFD700" />
        </Link>
      ) : null,
    [dropHand, handUp]
  )
  // }

  const access = React.useMemo(() => {
    return {
      canSeeUserList: {
        name: t("View Participant List"),
        value: UserPermission.canSeeUserList,
        hasPermission: hasPermission(
          user!.permissions,
          UserPermission.canSeeUserList
        ),
        togglePermission: () =>
          meetingClient?.togglePermission(
            id,
            permissions,
            UserPermission.canSeeUserList
          )
      },
      canUseChat: {
        name: t("View Chat"),
        value: UserPermission.canUseChat,
        hasPermission: hasPermission(
          user!.permissions,
          UserPermission.canUseChat
        ),
        togglePermission: () =>
          meetingClient?.togglePermission(
            id,
            permissions,
            UserPermission.canUseChat
          )
      },
      canRaiseHand: {
        name: t("Allow Raise Hand"),
        value: UserPermission.canRaiseHand,
        hasPermission: hasPermission(
          user!.permissions,
          UserPermission.canRaiseHand
        ),
        togglePermission: () =>
          meetingClient?.togglePermission(
            id,
            permissions,
            UserPermission.canRaiseHand
          )
      },
      canUseMic: {
        name: t("Allow microphone"),
        value: UserPermission.canUseMic,
        hasPermission: hasPermission(
          user!.permissions,
          UserPermission.canUseMic
        ),
        togglePermission: () =>
          meetingClient?.togglePermission(
            id,
            permissions,
            UserPermission.canUseMic,
            micEnabled
          )
      },
      canUseCam: {
        name: t("Allow camera"),
        value: UserPermission.canUseCam,
        hasPermission: hasPermission(
          user!.permissions,
          UserPermission.canUseCam
        ),
        togglePermission: () => {
          meetingClient?.togglePermission(
            id,
            permissions,
            UserPermission.canUseCam,
            camEnabled
          )
          if (
            hasPermission(user!.permissions, UserPermission.canUseCam) &&
            camEnabled
          ) {
            meetingClient?.toggleCam(camEnabled, id)
          }
        }
      },
      canUseScreenshare: {
        name: t("Allow screen share"),
        value: UserPermission.canUseScreenshare,
        hasPermission: hasPermission(
          user!.permissions,
          UserPermission.canUseScreenshare
        ),
        togglePermission: () =>
          meetingClient?.togglePermission(
            id,
            permissions,
            UserPermission.canUseScreenshare,
            isShareScreen
          )
      },
      canSeeSettingButton: {
        name: t("Allow Settings Button"),
        value: UserPermission.canSeeSettingButton,
        hasPermission: hasPermission(
          user!.permissions,
          UserPermission.canSeeSettingButton
        ),
        togglePermission: () =>
          meetingClient?.togglePermission(
            id,
            permissions,
            UserPermission.canSeeSettingButton
          )
      }
    }
  }, [
    t,
    user,
    meetingClient,
    id,
    permissions,
    micEnabled,
    camEnabled,
    isShareScreen
  ])

  const accessListModerator: accessList[] = []
  const accessListBroadcaster: accessList[] = React.useMemo(
    () => [access.canUseMic, access.canUseCam, access.canUseScreenshare],
    [access.canUseCam, access.canUseMic, access.canUseScreenshare]
  )
  const accessListAudience: accessList[] = React.useMemo(
    () => [access.canUseMic, access.canUseCam, access.canUseScreenshare],
    [access.canUseCam, access.canUseMic, access.canUseScreenshare]
  )

  const styles = React.useMemo(() => {
    return {
      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0
    }
  }, [])

  if (type === UserType.MODERATOR) {
    return (
      <UserPanel
        camDisplay={camDisplay}
        micDisplay={micDisplay}
        handRaisedDisplay={handRaisedDisplay}
        userName={name}
        userId={userId}
        avatar={avatar}
        expandedMenu={_localUserId === userId ? false : true}
        isLast={isLast}
        accessList={accessListModerator}
        color={color}
        isAudience={isAudience}
        isModerator={isModerator}
      />
    )
  }

  if (type === UserType.BROADCASTER) {
    return (
      <UserPanel
        camDisplay={camDisplay}
        micDisplay={micDisplay}
        isAudience={isAudience}
        isModerator={isModerator}
        handRaisedDisplay={handRaisedDisplay}
        userName={name}
        userId={userId}
        color={color}
        avatar={avatar}
        expandedMenu={true}
        isLast={isLast}
        accessList={accessListBroadcaster}
        expandedStyles={styles}
      />
    )
  }

  return (
    <UserPanel
      camDisplay={camDisplay}
      micDisplay={micDisplay}
      handRaisedDisplay={handRaisedDisplay}
      userName={name}
      color={color}
      avatar={avatar}
      userId={userId}
      expandedMenu={true}
      isLast={isLast}
      accessList={accessListAudience}
      expandedStyles={styles}
      isAudience={isAudience}
      isModerator={isModerator}
    />
  )
}

export const UserModeratorView = React.memo(_UserModeratorView)
